import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Box,
  Grid,
  Typography
} from '@mui/material';
import { Close as CloseIcon, Category as CategoryIcon } from '@mui/icons-material';
import { AddEditFeaturesCategory } from 'src/ApiServices';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';

const AddEditFeaturesCategoryModal = ({ open, onClose, onSuccess, category }) => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || ''
      });
    } else {
      setFormData({ name: '', description: '' });
    }
    setErrors({});
  }, [category, open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description
      };
      if (category) payload.id = category.id;

      await AddEditFeaturesCategory(payload);
      toast.success(`Category ${category ? 'updated' : 'created'} successfully!`);
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('API Error:', error);
      toast.error(
        error.response?.data?.message || `Failed to ${category ? 'update' : 'create'} category!`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '' });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: 'white',
          py: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CategoryIcon />
            <Typography
              variant="h6"
              fontFamily="poppinsSemiBold"
              sx={{
                fontSize: { xs: 12, sm: 20 }
              }}
            >
              {category ? 'Edit Feature Category' : 'Add New Feature Category'}
            </Typography>
          </Box>
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              minWidth: 'auto',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                required
                variant="outlined"
              />
              {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
            </Grid>

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={4}
                variant="outlined"
                placeholder="Enter category description (optional)"
              />
            </Grid> */}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ fontFamily: 'poppinsMedium' }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              fontFamily: 'poppinsMedium',
              backgroundColor: palette.primary.main,
              '&:hover': {
                backgroundColor: palette.primary.dark
              }
            }}
            disabled={loading}
          >
            {loading ? 'Saving...' : category ? 'Update Category' : 'Add Category'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditFeaturesCategoryModal;
